import { useMemo } from 'react';

import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/ui/tooltip';
import classNames from 'classnames';

import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { IconPill } from './IconPill';

interface StageConfig {
  definition: string;
  displayStage: string;
  reasons: string[];
  status: string;
}

const useStageConfig = (stage?: number): StageConfig => {
  const { t } = useLingui();

  return useMemo(() => {
    switch (stage) {
      case 1:
        return {
          displayStage: '1',
          status: t`Initial`,
          definition: t`Early periodontitis with minimal tissue and bone loss.`,
          reasons: [
            t`CAL 1-2 mm`,
            t`Bone loss <15% (coronal third)`,
            t`Probing depths ≤4 mm`,
            t`No tooth loss`,
          ],
        };
      case 2:
        return {
          displayStage: '2',
          status: t`Moderate`,
          definition: t`Established disease with moderate attachment and bone loss.`,
          reasons: [
            t`CAL 3-4 mm`,
            t`Bone loss 15-33% (coronal third)`,
            t`Probing depths ≤5 mm`,
            t`No tooth loss`,
          ],
        };
      case 3:
      case 4:
        return {
          displayStage: '3+',
          status: t`Severe/Advanced`,
          definition: t`Advanced disease with high risk for tooth loss and increased complexity. Includes stages 3 and 4.`,
          reasons: [
            t`CAL ≥5 mm`,
            t`Bone loss to middle third or beyond`,
            t`Probing depths ≥6 mm or furcation involvement`,
            t`Functional complexity (mobility, bite collapse, or <20 teeth)`,
          ],
        };
      default:
        return {
          displayStage: 'N/A',
          status: t`No Periodontitis Detected`,
          definition: t`Gums and supporting tissues show no signs of periodontitis. Bone levels and attachment are within a healthy range.`,
          reasons: [
            t`No clinical attachment loss consistent with periodontitis`,
            t`No radiographic bone loss related to periodontal disease`,
            t`Probing depths and tissue response fall within healthy limits`,
          ],
        };
    }
  }, [stage, t]);
};

interface Props {
  examDate: string | null;
  hasCalculus: boolean;
  isPerioPatient: boolean;
  mode?: 'dark' | 'light';
  numberOfQuadrantsWithPerioRecs: number;
  stage?: number;
  toothCount: number;
}

export const StageIconPill = ({
  examDate,
  hasCalculus,
  isPerioPatient,
  mode = 'dark',
  numberOfQuadrantsWithPerioRecs,
  stage,
  toothCount,
}: Props) => {
  const { t } = useLingui();
  const { trackEvent } = useTrackEvent();
  const stageConfig = useStageConfig(stage);

  return (
    <Tooltip
      onOpenChange={(open) => {
          if (open) {
            trackEvent(mixpanelEvents.perioChartStageTooltipOpen, {
              perioStage: stage ?? 'N/A',
              examDate,
              hasCalculusAtBalancedThreshold: hasCalculus,
              isPerioPatient,
              numberOfQuadrantsWithPerioRecs,
              toothCount,
            });
          }
        }}
      >
        <TooltipTrigger
          className="vai-flex vai-items-center vai-justify-center"
          tabIndex={-1} // tabIndex prevents tooltip from opening on focus
        >
          <IconPill
            mode={mode}
            text={stage ? t`Stage` : t`Stage N/A`}
            icon={
              stage ? (
                <div
                  className={classNames(
                    'vai-min-w-3 vai-px-[2px] vai-py-[1px] vai-rounded-full vai-flex vai-items-center vai-justify-center',
                    {
                      'vai-bg-yellow-400': stage === 1,
                      'vai-bg-tangerine-400': stage === 2,
                      'vai-bg-blaze-500': stage === 3 || stage === 4,
                      'vai-text-zinc-700': mode === 'dark',
                      'vai-text-zinc-50': mode === 'light',
                    },
                  )}
                >
                  <span className="vai-font-inter vai-text-[9px] vai-font-bold vai-leading-[100%]">
                    {stageConfig.displayStage}
                  </span>
                </div>
              ) : null
            }
          />
        </TooltipTrigger>
        <TooltipContent
          className={classNames('vai-w-80', {
            'vai-text-zinc-50': mode === 'dark',
          })}
          variant={mode === 'dark' ? 'dark' : 'default'}
        >
          <p className="vai-font-inter vai-text-sm vai-font-normal">
            <Trans>Stage </Trans>
            {stageConfig.displayStage}:{' '}
            <span className="vai-font-semibold">{stageConfig.status}</span>
          </p>

          <p className="vai-mt-2 vai-font-inter !vai-text-[12px] !vai-font-semibold">
            <Trans>What it means</Trans>
          </p>
          <p className="vai-font-inter !vai-text-[12px] !vai-font-normal">
            {stageConfig.definition}
          </p>

          <p className="vai-mt-4 vai-font-inter !vai-text-[12px] !vai-font-semibold">
            <Trans>Why assigned</Trans>
          </p>
          <ul className="vai-list-disc vai-pl-4 vai-font-inter !vai-text-[12px] vai-font-normal">
            {stageConfig.reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>

          <a
            onClick={() => {
              trackEvent(mixpanelEvents.perioChartAAPGuidelinesClicked, {
                perioStage: stage ?? 'N/A',
                examDate,
                hasCalculusAtBalancedThreshold: hasCalculus,
                isPerioPatient,
                numberOfQuadrantsWithPerioRecs,
                toothCount,
              });
            }}
            className="vai-mt-4 vai-font-inter vai-text-[12px] vai-underline vai-text-sapphire-600 vai-flex vai-items-center vai-gap-1"
            href="https://www.perio.org/wp-content/uploads/2019/08/Staging-and-Grading-Periodontitis.pdf"
            target="_blank"
            rel="noreferrer"
          >
            <Trans>AAP Guidelines</Trans>
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          </a>
        </TooltipContent>
      </Tooltip>
  );
};
